import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";
import "./learningStyle.css";
import { selectAdminToken } from "../../admin/sessionSlice";
import { fetchLsSettings } from "../../functions/fetchLsSettings";
import { checkLsPlayed } from "../../functions/checkLsPlayed";
import { lsPlayedKey, lsLastResultIdKey } from "../../functions/lsPlayedFlag";
import { useDocumentTitle } from "../../functions/useDocumentTitle";

const USER_DATA_KEY = "userData";
function readSessionUserData() {
  try {
    return JSON.parse(sessionStorage.getItem(USER_DATA_KEY) || "{}") || {};
  } catch {
    return {};
  }
}

const s3Url = (key) => (key ? `${process.env.REACT_APP_S3_PATH || ""}${key}` : "");

/** Fixed pastel palette, cycled by rule position — same palette used as the Quiz option-card fallback. */
const OPTION_COLORS = ["#f8bfd2", "#bfe8bc", "#f0d9a3", "#bcc9f2", "#ddc2f2", "#bdeee3"];

/** Option-box colors are pastel (meant as light card backgrounds) — re-saturate + darken for a vivid icon stroke. */
function darkenColor(hex, minSaturation = 0.6, maxLightness = 0.46) {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || "");
  if (!m) return hex;
  const num = parseInt(m[1], 16);
  const r = ((num >> 16) & 255) / 255;
  const g = ((num >> 8) & 255) / 255;
  const b = (num & 255) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;
  const dLight = max - min;
  if (dLight !== 0) {
    s = dLight / (1 - Math.abs(2 * l - 1));
    switch (max) {
      case r: h = ((g - b) / dLight) % 6; break;
      case g: h = (b - r) / dLight + 2; break;
      default: h = (r - g) / dLight + 4;
    }
    h *= 60;
    if (h < 0) h += 360;
  }

  const outS = Math.max(s, minSaturation);
  const outL = Math.min(l, maxLightness);

  const c = (1 - Math.abs(2 * outL - 1)) * outS;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const mAdj = outL - c / 2;
  let [r1, g1, b1] = [0, 0, 0];
  if (h < 60) [r1, g1, b1] = [c, x, 0];
  else if (h < 120) [r1, g1, b1] = [x, c, 0];
  else if (h < 180) [r1, g1, b1] = [0, c, x];
  else if (h < 240) [r1, g1, b1] = [0, x, c];
  else if (h < 300) [r1, g1, b1] = [x, 0, c];
  else [r1, g1, b1] = [c, 0, x];

  const toHex = (v) => Math.round((v + mAdj) * 255).toString(16).padStart(2, "0");
  return `#${toHex(r1)}${toHex(g1)}${toHex(b1)}`;
}

function BulbIcon({ color }) {
  return (
    <svg className="ls-rules-bulb" viewBox="-4 -4 32 32" style={{ color }}>
      {/* Sparkle rays around the bulb */}
      <g stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
        <path d="M4.2 3.2l1.6 1.6" />
        <path d="M-1 9h2.3" />
        <path d="M19.8 3.2l-1.6 1.6" />
        <path d="M25 9h-2.3" />
        <path d="M2 15.5h2" />
        <path d="M22 15.5h-2" />
      </g>
      <path
        d="M12 2.5a6.5 6.5 0 0 0-3.8 11.8c.7.5 1.1 1.3 1.1 2.1V17h5.4v-.6c0-.8.4-1.6 1.1-2.1A6.5 6.5 0 0 0 12 2.5z"
        fill="currentColor"
        fillOpacity="0.18"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M9.5 19.5h5M10.2 21.5h3.6" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M13 6.7l-3 4.1h2.1l-1.1 3.3 3.3-4.3h-2.1z" fill="currentColor" stroke="none" />
    </svg>
  );
}

function Rules() {
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useDocumentTitle(settings?.tab_name);

  useEffect(() => {
    if (!adminToken) {
      navigate("/login");
      return;
    }
    (async () => {
      try {
        // A browser Back press after finishing can land here — bounce straight to
        // Thank You instead of showing Rules again (Quiz has the same guard, but
        // catching it here skips the pointless Rules -> Quiz -> redirect hop).
        const userData = readSessionUserData();
        const userIdForCheck = userData?.userId || userData?.userid || userData?.id || null;
        const playedCheck = await checkLsPlayed({ token: adminToken, userId: userIdForCheck });
        if (playedCheck?.played) {
          sessionStorage.setItem(lsPlayedKey(adminToken), "true");
          if (playedCheck.id) sessionStorage.setItem(lsLastResultIdKey(adminToken), String(playedCheck.id));
          navigate("/thankyou", { replace: true });
          return;
        }

        const res = await fetchLsSettings({ token: adminToken });
        setSettings(res.settings || null);
      } catch (e) {
        Swal.fire("Error", e.message || "Failed to load rules", "error");
      } finally {
        setLoading(false);
      }
    })();
  }, [adminToken, navigate]);

  if (loading) {
    return (
      <div className="ls-page">
        <div className="ls-loading">Loading...</div>
      </div>
    );
  }

  const rules = Array.isArray(settings?.rules) && settings.rules.length > 0
    ? settings.rules
    : ["Answer each question honestly to discover your learning style."];

  const optionColors = [1, 2, 3, 4, 5, 6].map((n) =>
    darkenColor(settings?.[`option_box_color${n}`] || OPTION_COLORS[n - 1])
  );

  return (
    <div
      className="ls-page ls-rules-page"
      style={{
        "--ls-text-color": settings?.text_color || "#1f2937",
        "--ls-button-color": settings?.button_color || "#e07b39",
        "--ls-button-text-color": settings?.button_text_color || "#ffffff",
      }}
    >
      {settings?.logo && <img src={s3Url(settings.logo)} className="ls-logo" alt="Logo" />}

      <div className="ls-rules-body">
        <span className="ls-rules-badge">Rules</span>
        <ul className="ls-rules-list">
          {rules.map((r, i) => (
            <li key={i}>
              <BulbIcon color={optionColors[i % optionColors.length]} />
              <span className="ls-rules-text">{r}</span>
            </li>
          ))}
        </ul>
      </div>

      <button
        type="button"
        className="ls-primary-btn ls-rules-next-btn"
        onClick={() => navigate("/quiz")}
      >
        Next
      </button>
    </div>
  );
}

export default Rules;
